import type { BucketWrite, BucketWriteMap } from "./bucket_write_map";
import type {
  BucketAddress,
  MagIndex,
  SegmentBucketData,
  SegmentId,
  VoxelIndex,
} from "./volume_annotation_types";

/**
 * A run of consecutive voxel indices sharing one value. Every run a transaction
 * produces is constant-valued, because transactions are single-valued.
 */
export interface VoxelRun {
  start: VoxelIndex;
  length: number;
  value: SegmentId;
}

export interface BucketDiff {
  address: BucketAddress;
  runs: VoxelRun[];
}

export type TransactionId = string;

export interface TransactionDiff {
  id: TransactionId;
  /** Monotonic per client; the merge key in a future collaborative mode. */
  sequence: number;
  /** The mag the user authored at; every other mag's diffs are resampled. */
  sourceMagIndex: MagIndex;
  toolName: string;
  bucketDiffs: BucketDiff[];
}

/**
 * Extract runs from a bucket's writes. A word scan over the mask — no sort and
 * no per-voxel value lookup, because the value is held once for the bucket.
 */
export function toRuns(write: BucketWrite): VoxelRun[] {
  const runs: VoxelRun[] = [];
  for (const { start, length } of write.mask.runs()) {
    runs.push({ start, length, value: write.value });
  }
  return runs;
}

/**
 * Apply one run to a bucket array of any segmentation element class. Absolute
 * writes, hence idempotent. `run.value` is a SegmentId and therefore always a
 * bigint, so a non-64-bit array needs it converted.
 */
export function applyRun(data: SegmentBucketData, run: VoxelRun): void {
  if (data instanceof BigUint64Array || data instanceof BigInt64Array) {
    data.fill(run.value, run.start, run.start + run.length);
    return;
  }
  (data as Uint32Array).fill(Number(run.value), run.start, run.start + run.length);
}

export function bucketDiffsOf(bucketWriteMaps: Iterable<BucketWriteMap>): BucketDiff[] {
  const diffs: BucketDiff[] = [];
  for (const bucketWriteMap of bucketWriteMaps) {
    for (const entry of bucketWriteMap.values()) {
      if (entry.write.mask.count === 0) continue;
      diffs.push({ address: entry.address, runs: toRuns(entry.write) });
    }
  }
  return diffs;
}

/** Total voxels a diff touches. Used by tests. */
export function countDiffVoxels(diff: TransactionDiff): number {
  let total = 0;
  for (const bucketDiff of diff.bucketDiffs) {
    for (const run of bucketDiff.runs) total += run.length;
  }
  return total;
}

/**
 * Binary run encoding, little-endian. Every run in a bucket carries the same
 * value, so the value is hoisted into the header and a run is just 4 bytes:
 *
 *   uint64  value
 *   uint32  runCount
 *   repeat: uint16 startIndex, uint16 length
 *
 * Encoded through a DataView because the field widths are mixed and DataView
 * takes an explicit littleEndian flag, so the format does not silently inherit
 * the platform's byte order.
 */
export function encodeBucketDiff(diff: BucketDiff): Uint8Array {
  const value = diff.runs.length > 0 ? diff.runs[0].value : 0n;
  const buffer = new ArrayBuffer(8 + 4 + diff.runs.length * 4);
  const view = new DataView(buffer);
  view.setBigUint64(0, value, true);
  view.setUint32(8, diff.runs.length, true);
  let offset = 12;
  for (const run of diff.runs) {
    if (run.value !== value) {
      // The header holds one value for the whole bucket, so a mixed-value diff
      // would silently serialize every run with the first one's value. A
      // BucketDiff must come from a single transaction, which §4 guarantees is
      // single-valued; producing one by merging transactions is the bug.
      throw new Error(
        `encodeBucketDiff got runs with mixed values (${value} and ${run.value}). ` +
          "A BucketDiff must cover exactly one transaction.",
      );
    }
    view.setUint16(offset, run.start, true);
    view.setUint16(offset + 2, run.length, true);
    offset += 4;
  }
  return new Uint8Array(buffer);
}

export function decodeBucketDiff(bytes: Uint8Array): VoxelRun[] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const value = view.getBigUint64(0, true);
  const runCount = view.getUint32(8, true);
  const runs: VoxelRun[] = [];
  for (let i = 0; i < runCount; i++) {
    const offset = 12 + i * 4;
    runs.push({
      start: view.getUint16(offset, true),
      length: view.getUint16(offset + 2, true),
      value,
    });
  }
  return runs;
}
